import React, { useMemo, useState } from 'react';
import Popover from '@rsos/components/capstone/base/Popover';
import {
  StyledIconButton,
  StyledBubble,
  StyledInfoIcon,
} from './InfoTipStyles';

export type InfoTipProps = {
  /**
   * 'inverted' tooltips will have a dark background in the light theme, and a
   * light background in the dark theme.
   *
   * 'matched' tooltips will match the
   * foreground/background of their surroundings: light with light, dark with
   * dark.
   *
   * The default is 'inverted'. */
  appearance?: 'inverted' | 'matched';
  triggerOn?: 'click' | 'hover';
  popoverID?: string;
  isOpen?: boolean;
  xOffset?: number;
  yOffset?: number;
  width?: string;
  cssDisplayProperty?: string;
  zIndex?: number;
};

export const InfoTip = ({
  appearance = 'inverted',
  triggerOn = 'hover',
  children,
  width,
  popoverID,
  // Modals are at 200; for tooltips to function in modals, they need a lil boost.
  zIndex = 200,
  ...props
}: React.PropsWithChildren<InfoTipProps>) => {
  const [isOpen, setIsOpen] = useState(false);
  const id = useMemo(
    () => popoverID || Math.random().toString(36).slice(-5),
    [popoverID],
  );

  const triggers =
    triggerOn === 'click'
      ? {
          onClick: () => {
            setIsOpen(open => !open);
          },
        }
      : {
          onMouseEnter: () => setIsOpen(true),
          onMouseLeave: () => setIsOpen(false),
        };

  return (
    <Popover
      isOpen={isOpen}
      xOffset={8}
      yOffset={14}
      cssDisplayProperty="inline"
      popoverID={id}
      zIndex={zIndex}
      trigger={
        <StyledIconButton {...triggers}>
          <StyledInfoIcon />
        </StyledIconButton>
      }
      {...props}
    >
      <StyledBubble appearance={appearance} width={width}>
        {children}
      </StyledBubble>
    </Popover>
  );
};

export default InfoTip;
